/**
 * The one network call this bundle makes. `apps/marketing-web` was backend-free until GH-894 —
 * the base URL is wired the same way `apps/donor-web` reads its API origin: a build-time
 * `VITE_API_BASE_URL`, baked in by `infra/Dockerfile.marketing` and passed by `infra/deploy.sh`'s
 * `marketing` target, the same `--build-arg` name and pattern as the `web` target uses.
 *
 * The endpoint contract (owner-specified, built in parallel by another agent against this exact
 * shape — this file does not need it deployed to be correct against it):
 *
 *   POST /v1/public/sales-leads
 *   { name, email, organizationName, phone?, message?, source: "marketing_site" }
 *   -> 201 { id: string, status: "received" }
 *   -> 400 with field errors on validation failure
 *   -> 429 when rate-limited
 *
 * This bundle holds no secret and computes nothing the server must trust — it sends intent and
 * renders what the server returns, same posture as donor-web's `api.ts`.
 */
const API_BASE: string = import.meta.env["VITE_API_BASE_URL"] ?? "http://localhost:8080";

export interface SalesLeadInput {
  name: string;
  email: string;
  organizationName: string;
  phone?: string;
  message?: string;
}

export interface SalesLeadResponse {
  id: string;
  status: "received";
}

export type SalesLeadResult =
  | { outcome: "success"; data: SalesLeadResponse }
  | { outcome: "validation_error"; message: string }
  | { outcome: "rate_limited" }
  | { outcome: "error"; message: string };

/**
 * The server's real error envelope (`services/api/src/app.ts`'s `respondWithError`, the same
 * shape every GiveHere route answers with): `{ error: { code, message, retryability,
 * correlationId } }`. It never echoes which field was rejected — `request-validation.ts` logs
 * the field list server-side only, by design, so a rejected body is never reflected back to the
 * caller — so there is no field-level detail to surface here, only the one message.
 */
async function describeValidationError(response: Response): Promise<{ message: string }> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "error" in body) {
      const error = (body as { error: unknown }).error;
      if (
        error &&
        typeof error === "object" &&
        typeof (error as { message?: unknown }).message === "string"
      ) {
        return { message: (error as { message: string }).message };
      }
    }
  } catch {
    // Body was not JSON — fall through to the generic message below.
  }
  return { message: "Please check your details and try again." };
}

export async function submitSalesLead(input: SalesLeadInput): Promise<SalesLeadResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/v1/public/sales-leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, source: "marketing_site" }),
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Could not reach the server.";
    return { outcome: "error", message: `Could not reach the server. ${message}`.trim() };
  }

  if (response.status === 201) {
    const data = (await response.json()) as SalesLeadResponse;
    return { outcome: "success", data };
  }
  if (response.status === 400) {
    const { message } = await describeValidationError(response);
    return { outcome: "validation_error", message };
  }
  if (response.status === 429) {
    return { outcome: "rate_limited" };
  }
  return { outcome: "error", message: `Unexpected response from the server (${response.status}).` };
}
